import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './client';

import type {
  ApprovalItem,
  BoardLane,
  CashPosition,
  DocumentView,
  Guarantee,
  Instalment,
  JobDetail,
  JobSummary,
  Me,
  StageRef,
  Track,
  Customer,
} from './types';

/**
 * Every query key starts with its resource, so a mutation can invalidate a whole area with
 * one prefix rather than listing the keys it happened to know about.
 */
export const keys = {
  me: ['me'] as const,
  board: ['jobs', 'board'] as const,
  jobs: (params?: unknown) => ['jobs', 'list', params] as const,
  job: (id: string) => ['jobs', id] as const,
  stages: ['stages'] as const,
  tracks: ['tracks'] as const,
  customers: ['customers'] as const,
  documents: (jobId: string) => ['documents', jobId] as const,
  instalments: (jobId: string) => ['money', jobId, 'instalments'] as const,
  cash: (jobId: string) => ['money', jobId, 'cash'] as const,
  guarantees: (jobId: string) => ['money', jobId, 'guarantee'] as const,
  approvals: (filter: string) => ['approvals', filter] as const,
  jobApprovals: (jobId: string) => ['approvals', 'job', jobId] as const,
};

/* ── session ─────────────────────────────────────────────────────────────── */

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/v1/auth/me'),
    /* One 401 is the answer, not a hiccup: it means "signed out", and the shell routes on it. */
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Which sign-in methods this install actually has. Asked before the screen renders so a
 * Google button is never shown on an install that cannot answer it (spec 12, no dead ends).
 */
export function useAuthProviders() {
  return useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: () => api.get<{ password: boolean; google: boolean }>('/v1/auth/providers'),
    retry: false,
    staleTime: Infinity,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ user: Me['user'] }>('/v1/auth/login', input),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/v1/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}

/* ── pipeline ────────────────────────────────────────────────────────────── */

export function useBoard() {
  return useQuery({
    queryKey: keys.board,
    queryFn: () => api.get<{ lanes: BoardLane[] }>('/v1/jobs/board'),
    select: (data) => data.lanes,
  });
}

export function useStages() {
  return useQuery({
    queryKey: keys.stages,
    queryFn: () => api.get<{ items: StageRef[] }>('/v1/admin/stages'),
    select: (data) => data.items,
    /* The pipeline changes when an admin edits it, which is rare. */
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * Filters mirror `GET /jobs` (spec 02 "Jobs"): `q`/`status` plus the same dimension names the
 * pivot's drill-down cells carry (spec 08 §7) — `track`, `phase`, `stage`, `owner`, `overSla` —
 * so a pivot cell's `filter` object can be spread straight into this hook's params.
 */
export function useJobs(
  params: {
    q?: string;
    status?: string;
    track?: string;
    phase?: string;
    stage?: string;
    owner?: string;
    overSla?: boolean;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: keys.jobs(params),
    queryFn: () => {
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) search.set(k, String(v));
      const qs = search.toString();
      return api.get<{ items: JobSummary[]; total: number }>(`/v1/jobs${qs ? `?${qs}` : ''}`);
    },
  });
}

/** The tracks a new job can start on. Rarely changes, so it is cached like the pipeline. */
export function useTracks() {
  return useQuery({
    queryKey: keys.tracks,
    queryFn: () => api.get<{ items: Track[] }>('/v1/jobs/tracks'),
    select: (data) => data.items,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useCustomers() {
  return useQuery({
    queryKey: keys.customers,
    queryFn: () => api.get<{ items: Customer[] }>('/v1/customers'),
    select: (data) => data.items,
    retry: false,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: 'person' | 'company'; name: string }) =>
      api.post<{ id: string }>('/v1/customers', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.customers }),
  });
}

export interface CreateJobInput {
  customerId: string;
  title: string;
  trackId: string;
  siteAddress?: string;
  scopeText?: string;
  valueExVatSatang: number;
  vatSatang: number;
  valueIncVatSatang: number;
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJobInput) =>
      api.post<{ id: string; code: string }>('/v1/jobs', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['jobs'] }),
  });
}

export function useJob(jobId: string) {
  return useQuery({
    queryKey: keys.job(jobId),
    queryFn: () => api.get<JobDetail>(`/v1/jobs/${jobId}`),
    enabled: Boolean(jobId),
  });
}

export function useMoveStage(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { toStageId: string; expectedUpdatedAt: string | number; note?: string }) =>
      api.post<{ stageId: string }>(`/v1/jobs/${jobId}/stage`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

/* ── approvals ───────────────────────────────────────────────────────────── */

export function useApprovals(
  filter: 'pending' | 'decided' | 'all' = 'pending',
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: keys.approvals(filter),
    queryFn: () => api.get<{ items: ApprovalItem[] }>(`/v1/approvals?filter=${filter}`),
    select: (data) => data.items,
    /* The shell's badge asks for this on every page, so it must not fire before the session
       is known — otherwise a signed-out visitor's console fills with 401s from a request
       whose answer was never going to be used. */
    enabled,
  });
}

export function useJobApprovals(jobId: string) {
  return useQuery({
    queryKey: keys.jobApprovals(jobId),
    queryFn: () => api.get<{ items: ApprovalItem[] }>(`/v1/jobs/${jobId}/approvals`),
    select: (data) => data.items,
    enabled: Boolean(jobId),
  });
}

export function useRequestApproval(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: unknown) =>
      api.post<{ id: string }>(`/v1/jobs/${jobId}/approvals`, { payload }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; decision: 'approved' | 'rejected'; note?: string }) =>
      api.post(`/v1/approvals/${input.id}/decide`, {
        decision: input.decision,
        note: input.note,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/* ── documents ───────────────────────────────────────────────────────────── */

export function useDocuments(jobId: string) {
  return useQuery({
    queryKey: keys.documents(jobId),
    queryFn: () => api.get<{ documents: DocumentView[] }>(`/v1/jobs/${jobId}/documents`),
    select: (data) => data.documents,
    enabled: Boolean(jobId),
    retry: false,
  });
}

export interface DraftLineInput {
  description: string;
  qtyMilli?: number;
  unit?: string | null;
  unitPriceSatang?: number;
}

export function useCreateDocument(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { docTypeKey: string; lines: DraftLineInput[] }) =>
      api.post<{ document: DocumentView }>(`/v1/jobs/${jobId}/documents`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents', jobId] }),
  });
}

export function useIssueDocument(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<{ document: DocumentView }>(`/v1/documents/${documentId}/issue`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents', jobId] });
      /* Issuing a contract creates the payment schedule, so the money tab is stale too. */
      void qc.invalidateQueries({ queryKey: ['money', jobId] });
    },
  });
}

export function useTransitionDocument(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; to: 'sent' | 'accepted' | 'rejected' | 'void' }) =>
      api.post<{ document: DocumentView }>(`/v1/documents/${input.documentId}/transition`, {
        to: input.to,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents', jobId] }),
  });
}

export function useDeleteDraft(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.delete(`/v1/documents/${documentId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['documents', jobId] }),
  });
}

/* ── money ───────────────────────────────────────────────────────────────── */

export function useInstalments(jobId: string) {
  return useQuery({
    queryKey: keys.instalments(jobId),
    queryFn: () => api.get<{ items: Instalment[] }>(`/v1/jobs/${jobId}/instalments`),
    select: (data) => data.items,
    enabled: Boolean(jobId),
    retry: false,
  });
}

export function useCashPosition(jobId: string) {
  return useQuery({
    queryKey: keys.cash(jobId),
    queryFn: () => api.get<CashPosition>(`/v1/jobs/${jobId}/cash`),
    enabled: Boolean(jobId),
    retry: false,
  });
}

export function useGuarantees(jobId: string) {
  return useQuery({
    queryKey: keys.guarantees(jobId),
    queryFn: () => api.get<{ items: Guarantee[] }>(`/v1/jobs/${jobId}/guarantee`),
    select: (data) => data.items,
    enabled: Boolean(jobId),
    retry: false,
  });
}

/** Every money write invalidates the whole job's money area — the three views move together. */
function useMoneyMutation<TInput>(jobId: string, fn: (input: TInput) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['money', jobId] });
      void qc.invalidateQueries({ queryKey: ['documents', jobId] });
    },
  });
}

export function useInvoiceInstalment(jobId: string) {
  return useMoneyMutation<string>(jobId, (instalmentId) =>
    api.post(`/v1/instalments/${instalmentId}/invoice`),
  );
}

export function useRecordPayment(jobId: string) {
  return useMoneyMutation<{ instalmentId: string; amountSatang: number; reference?: string }>(
    jobId,
    ({ instalmentId, ...body }) => api.post(`/v1/instalments/${instalmentId}/payment`, body),
  );
}

export function useRecordGuarantee(jobId: string) {
  return useMoneyMutation<{ amountSatang: number; number?: string; bank?: string }>(jobId, (body) =>
    api.post(`/v1/jobs/${jobId}/guarantee`, body),
  );
}

export function useMoveCustody(jobId: string) {
  return useMoneyMutation<{ guaranteeId: string; to: 'returned' | 'cashed' }>(
    jobId,
    ({ guaranteeId, to }) => api.post(`/v1/guarantees/${guaranteeId}/custody`, { to }),
  );
}
